import type { InspectorDTO } from 'src/types/dto';
import type { CompetencyHierarchy } from 'src/utils/competency-hierarchy';
import type { IAuthorizationItem } from 'src/types/authorization';

import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { UserIcon } from 'src/components/user-icon';
import { AuthorizationsTooltip } from 'src/components/authorizations-tooltip';
import { InspectorDistanceCell } from 'src/components/visit-allocation-modal/components/inspector-distance-cell';
import { BusinessUnitCell } from 'src/components/visit-allocation-modal/components/business-unit-cell';
import { formatInspectorCityStateCountry } from 'src/utils/inspector-location';
import { PreferrenceType } from 'src/types/personnelPreferrence';
import { spacingUtil } from 'src/utils/spacing-util';

const ROW_HEIGHT = 60;

type Props = {
  rows: InspectorDTO[];
  loading?: boolean;
  distanceLoading?: boolean;
  emptyMessage?: string;
  siteLocation?: string;
  isDeskService?: boolean;
  accreditationBodyIds?: number[];
  activityCompetencyIds?: number[];
  competencyHierarchy?: CompetencyHierarchy;
  isTruncated?: boolean;
};

function getExclusionReasonKey(inspector: InspectorDTO): 'manuallyExcluded' | 'rotationLimitReached' {
  if (inspector.preferenceStatus === PreferrenceType.EXCLUDED) {
    return 'manuallyExcluded';
  }
  return 'rotationLimitReached';
}

function ExcludedInspectorsListComponent({
  rows,
  loading = false,
  distanceLoading = false,
  emptyMessage,
  siteLocation = '',
  isDeskService = false,
  accreditationBodyIds,
  activityCompetencyIds,
  competencyHierarchy,
  isTruncated = false,
}: Props) {
  const { t } = useTranslation('activities');

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('allocation.modal.grid.columns.name'),
        width: '22%',
      },
      {
        field: 'competencies',
        headerName: t('allocation.modal.grid.columns.competencies'),
        width: '34%',
      },
      {
        field: 'travel',
        headerName: t('allocation.modal.grid.columns.distance'),
        width: '10%',
      },
      {
        field: 'businessUnit',
        headerName: t('allocation.modal.grid.columns.businessUnit'),
        width: '10%',
      },
      {
        field: 'reason',
        headerName: t('allocation.details.excluded.columns.reason'),
        width: '24%',
      },
    ],
    [t]
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {loading ? (
        <LinearProgress
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}
        />
      ) : null}
      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ height: 32 }}>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  sx={{
                    width: column.width,
                    py: 0.5,
                    typography: 'subtitle2',
                    color: 'text.secondary',
                    fontWeight: 600,
                    bgcolor: 'background.paper',
                  }}
                >
                  {column.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 4, border: 0 }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {emptyMessage ?? t('allocation.details.excluded.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const reasonKey = getExclusionReasonKey(row);
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      height: ROW_HEIGHT,
                      maxHeight: ROW_HEIGHT,
                      '& td': { borderColor: 'divider' },
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: spacingUtil(1.5),
                          minWidth: 0,
                        }}
                      >
                        <UserIcon id={row.id} name={row.name} type={row.type} size="mini" />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap title={row.name}>
                            {row.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {formatInspectorCityStateCountry(row)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <AuthorizationsTooltip
                        containerIdentifier={`excluded-inspector-auth-${row.id}`}
                        accreditationBodyIds={accreditationBodyIds}
                        authorizations={row.authorizations as IAuthorizationItem[]}
                        activityCompetencyIds={activityCompetencyIds}
                        competencyHierarchy={competencyHierarchy}
                        wrapActiveWithInactiveTooltip
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <InspectorDistanceCell
                        row={row}
                        siteLocation={siteLocation}
                        isDeskService={isDeskService}
                        distanceLoading={distanceLoading}
                        showDirections={false}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <BusinessUnitCell inspector={row} />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Iconify
                          icon="eva:info-outline"
                          width={16}
                          sx={{ color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Typography variant="body2" noWrap>
                          {t(`allocation.details.excluded.reasons.${reasonKey}`)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isTruncated && !loading && rows.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'warning.lighter',
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify
              icon="eva:alert-triangle-outline"
              width={20}
              sx={{ color: 'warning.main', flexShrink: 0 }}
            />
            <Typography variant="body2" color="warning.dark">
              {t('allocation.details.excluded.truncatedMessage')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export const ExcludedInspectorsList = memo(ExcludedInspectorsListComponent);
ExcludedInspectorsList.displayName = 'ExcludedInspectorsList';
